import { createFileRoute } from "@tanstack/react-router";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { z } from "zod";

import { AdaStatLink } from "@/components/adastat-link";
import { AssetLabel } from "@/components/asset-label";
import { CopyValue } from "@/components/copy-value";
import { PlainValue } from "@/components/plain-value";
import { QueryState } from "@/components/query-state";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useGetArbitrages } from "@/generated/api/arbitrages/arbitrages";
import type { ArbitrageDisplayItem } from "@/generated/api/model";
import { ADA } from "@/lib/cardanoscan";
import { readDefaultMinProfitAda } from "@/lib/settings/min-profit";
import { utxoRef } from "@/lib/utxo-ref";
import { cn } from "@/lib/utils/cn";

// A malformed value falls back to the stored default rather than failing the page.
const searchSchema = z.object({
    min_profit_ada: z.number().nonnegative().optional().catch(undefined),
});

export const Route = createFileRoute("/arbitrages/")({ component: Arbitrages, validateSearch: searchSchema });

// The threshold is edited on /settings only; a `min_profit_ada` in the URL still overrides it,
// so a filtered view can be shared as a link.
function Arbitrages() {
    const search = Route.useSearch();
    const minProfitAda = search.min_profit_ada ?? readDefaultMinProfitAda();
    const arbitrages = useGetArbitrages({ min_profit_ada: minProfitAda });

    return (
        <QueryState
            query={arbitrages}
            isEmpty={(data) => data.arbitrages.length === 0}
            emptyMessage={`No arbitrage opportunities of at least ${minProfitAda} ADA right now.`}
        >
            {(data) => <ArbitrageList arbitrages={data.arbitrages} />}
        </QueryState>
    );
}

// Roughly a phone card with its UTXOs folded; each card is measured once mounted.
const CARD_HEIGHT_ESTIMATE = 180;
const CARD_GAP = 8;

// The list is usually short. Virtualizing it is a guard against a response that floods the page,
// so only the cards near the viewport are ever mounted.
// [[spec-virtualized-arbitrages-list-with-pool-utxos-collapsed-on-phones]]
function ArbitrageList({ arbitrages }: { arbitrages: ArbitrageDisplayItem[] }) {
    // The virtualizer mutates one instance in place, which the compiler's memoization would hide.
    "use no memo";
    const [listTop, setListTop] = useState(0);
    const virtualizer = useWindowVirtualizer({
        count: arbitrages.length,
        estimateSize: () => CARD_HEIGHT_ESTIMATE,
        gap: CARD_GAP,
        overscan: 5,
        scrollMargin: listTop,
        getItemKey: (index) => arbitrageKey(arbitrages[index]!),
    });
    const measureListTop = (list: HTMLDivElement | null) => {
        if (list) setListTop(list.getBoundingClientRect().top + window.scrollY);
    };

    return (
        <div ref={measureListTop} className="relative" style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((item) => (
                <div
                    key={item.key}
                    ref={virtualizer.measureElement}
                    data-index={item.index}
                    className="absolute inset-x-0 top-0"
                    style={{ transform: `translateY(${item.start - listTop}px)` }}
                >
                    <ArbitrageCard arbitrage={arbitrages[item.index]!} />
                </div>
            ))}
        </div>
    );
}

const arbitrageKey = (a: ArbitrageDisplayItem) =>
    `${utxoRef(a.buy_pool_tx_id, a.buy_pool_output_index)}>${utxoRef(a.sell_pool_tx_id, a.sell_pool_output_index)}`;

type Side = "Buy" | "Sell";

function AdaOn() {
    return (
        <span>
            <AssetLabel {...ADA} /> on
        </span>
    );
}

function PoolBadge({ side, name, tvl }: { side: Side; name: string; tvl: string }) {
    return (
        <Badge variant="outline" className="gap-1">
            <PlainValue value={name} label={`${side} pool`} />
            <span className="text-muted-foreground">
                (TVL <PlainValue value={tvl} label={`${side} pool TVL`} className="tabular-nums" />)
            </span>
        </Badge>
    );
}

function PoolUtxo({ side, txId, index }: { side: Side; txId: string; index: number }) {
    return (
        <div className="min-w-0">
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{side} pool UTXO</dt>
            <dd>
                <AdaStatLink kind="utxo" value={utxoRef(txId, index)} label={`${side} pool UTXO`} />
            </dd>
        </div>
    );
}

// Only what the operator pastes into a trade copies: the amounts and the token. Each pool UTXO
// opens its transaction on AdaStat instead.
function ArbitrageCard({ arbitrage }: { arbitrage: ArbitrageDisplayItem }) {
    return (
        <Card role="article" aria-label={`${arbitrage.buy_token_name} arbitrage`}>
            <CardHeader>
                <time className="font-mono text-sm">{arbitrage.timestamp}</time>
                <Badge variant="success">
                    <PlainValue value={arbitrage.profit_formatted} label="Profit" /> <AssetLabel {...ADA} />
                </Badge>
            </CardHeader>
            <CardContent className="space-y-2 text-[0.9375rem] leading-relaxed">
                <div className="flex flex-wrap items-center gap-1.5">
                    <span>Buy</span>
                    <CopyValue value={arbitrage.buy_receive_amount} label="Tokens bought">
                        <Badge variant="success">{arbitrage.buy_receive_amount}</Badge>
                    </CopyValue>
                    <AssetLabel id={arbitrage.buy_token_id} name={arbitrage.buy_token_name}>
                        <CopyValue value={arbitrage.buy_token_name} label="Token" className="font-semibold" />
                    </AssetLabel>
                    <span>for</span>
                    <CopyValue value={arbitrage.buy_amount} label="ADA spent">
                        <Badge variant="success">{arbitrage.buy_amount}</Badge>
                    </CopyValue>
                    <AdaOn />
                    <PoolBadge side="Buy" name={arbitrage.buy_pool} tvl={arbitrage.buy_pool_tvl} />
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    <ArrowRight className="size-3" />
                    <span>Sell for</span>
                    <CopyValue value={arbitrage.sell_receive_amount} label="ADA received">
                        <Badge variant="destructive">{arbitrage.sell_receive_amount}</Badge>
                    </CopyValue>
                    <AdaOn />
                    <PoolBadge side="Sell" name={arbitrage.sell_pool} tvl={arbitrage.sell_pool_tvl} />
                </div>
                <PoolUtxos arbitrage={arbitrage} />
            </CardContent>
        </Card>
    );
}

// A phone shows only the trade; the UTXOs fold behind a toggle so more cards fit on a screen.
// From `md` up there is room, so they always show and the toggle is hidden.
function PoolUtxos({ arbitrage }: { arbitrage: ArbitrageDisplayItem }) {
    const [open, setOpen] = useState(false);
    const listId = useId();

    return (
        <div className="border-t pt-1 md:pt-2">
            <button
                type="button"
                className="flex min-h-9 w-full items-center justify-between text-xs font-medium uppercase tracking-wide text-muted-foreground md:hidden"
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => setOpen(!open)}
            >
                Pool UTXOs
                <ChevronDown className={cn("size-4 transition-transform", !open && "-rotate-90")} aria-hidden />
            </button>
            <dl id={listId} className={cn("gap-2 pb-1 md:grid md:grid-cols-2 md:pb-0", open ? "grid" : "hidden")}>
                <PoolUtxo side="Buy" txId={arbitrage.buy_pool_tx_id} index={arbitrage.buy_pool_output_index} />
                <PoolUtxo side="Sell" txId={arbitrage.sell_pool_tx_id} index={arbitrage.sell_pool_output_index} />
            </dl>
        </div>
    );
}
